/**
 * Field Type Compatibility Utilities
 *
 * Determines if two Dataverse field types are compatible for field-to-field comparison
 * Based on Dataverse OData requirements and FieldMetadataService type mappings
 *
 * According to Microsoft Dataverse documentation:
 * "You can use comparison operators to compare property values in the same row.
 * Only comparison operators can be used to compare values in the same row,
 * and the column types must match."
 *
 * Reference: https://learn.microsoft.com/en-us/power-apps/developer/data-platform/webapi/query/filter-rows
 */

export interface FieldTypeInfo {
  attributeType?: string;
  logicalName: string;
  displayName?: string;
}

/**
 * Maps Dataverse AttributeType to a compatibility group
 * Fields in the same group can be compared with each other
 *
 * Based on api/src/lib/services/FieldMetadataService.ts mapAttributeTypeToODataType()
 */
export function getTypeCompatibilityGroup(attributeType?: string): string {
  if (!attributeType) return 'unknown';

  const type = attributeType.toLowerCase();

  // Integer types
  if (['integer', 'bigint'].includes(type)) {
    return 'integer';
  }

  // Decimal/floating point types
  if (['double', 'decimal', 'money'].includes(type)) {
    return 'decimal';
  }

  // String types
  if (['string', 'memo', 'entityname'].includes(type)) {
    return 'string';
  }

  // Boolean
  if (type === 'boolean') {
    return 'boolean';
  }

  // DateTime
  if (type === 'datetime') {
    return 'datetime';
  }

  // GUID types (including lookups which are stored as GUIDs)
  if (['uniqueidentifier', 'lookup', 'customer', 'owner'].includes(type)) {
    return 'uniqueidentifier';
  }

  // Picklist types (stored as integers in Dataverse)
  if (['picklist', 'state', 'status', 'multiselectpicklist'].includes(type)) {
    return 'picklist';
  }

  return 'unknown';
}

/**
 * Check if two fields have compatible types for comparison
 *
 * @param field1Type - AttributeType of first field
 * @param field2Type - AttributeType of second field
 * @returns true if fields can be compared in OData query
 */
export function areFieldTypesCompatible(
  field1Type?: string,
  field2Type?: string
): boolean {
  if (!field1Type || !field2Type) return false;

  const group1 = getTypeCompatibilityGroup(field1Type);
  const group2 = getTypeCompatibilityGroup(field2Type);

  // Unknown types are not compatible
  if (group1 === 'unknown' || group2 === 'unknown') return false;

  // Same group = compatible
  if (group1 === group2) return true;

  // Special compatibility rules for cross-group compatibility:

  // Integers and decimals can be compared (both are numeric types)
  if ((group1 === 'integer' && group2 === 'decimal') ||
      (group1 === 'decimal' && group2 === 'integer')) {
    return true;
  }

  // Picklists can be compared with integers (they're stored as integers in Dataverse)
  if ((group1 === 'picklist' && group2 === 'integer') ||
      (group1 === 'integer' && group2 === 'picklist')) {
    return true;
  }

  // Picklists can be compared with decimals (numeric comparison)
  if ((group1 === 'picklist' && group2 === 'decimal') ||
      (group1 === 'decimal' && group2 === 'picklist')) {
    return true;
  }

  return false;
}

/**
 * Filter a list of fields to only those compatible with a reference field
 *
 * @param referenceField - The field to compare against
 * @param allFields - All available fields
 * @returns Filtered list containing only compatible fields
 */
export function filterCompatibleFields<T extends FieldTypeInfo>(
  referenceField: FieldTypeInfo,
  allFields: T[]
): T[] {
  return allFields.filter(field =>
    areFieldTypesCompatible(
      referenceField.attributeType,
      field.attributeType
    )
  );
}

/**
 * Get a human-readable description of why two types are incompatible
 *
 * @param field1 - First field
 * @param field2 - Second field
 * @returns Error message if incompatible, null if compatible
 */
export function getIncompatibilityReason(
  field1: FieldTypeInfo,
  field2: FieldTypeInfo
): string | null {
  if (areFieldTypesCompatible(field1.attributeType, field2.attributeType)) {
    return null;
  }

  const type1Display = field1.attributeType || 'unknown';
  const type2Display = field2.attributeType || 'unknown';
  const name1 = field1.displayName || field1.logicalName;
  const name2 = field2.displayName || field2.logicalName;

  return `Cannot compare ${type1Display} field "${name1}" with ${type2Display} field "${name2}". ` +
    `Dataverse requires matching or compatible types for field-to-field comparisons.`;
}

/**
 * Get a user-friendly type group name for display
 *
 * @param attributeType - Dataverse AttributeType
 * @returns Display-friendly name for the type group
 */
export function getTypeGroupDisplayName(attributeType?: string): string {
  const group = getTypeCompatibilityGroup(attributeType);

  switch (group) {
    case 'integer':
      return 'Integer (also compatible with Decimal, Option Sets)';
    case 'decimal':
      return 'Decimal/Money (also compatible with Integer)';
    case 'string':
      return 'Text';
    case 'boolean':
      return 'Boolean (Yes/No)';
    case 'datetime':
      return 'Date/Time';
    case 'uniqueidentifier':
      return 'Lookup/GUID';
    case 'picklist':
      return 'Option Set (also compatible with Integer)';
    default:
      return 'Unknown type';
  }
}

/**
 * Get a short type group name for compact display
 *
 * @param attributeType - Dataverse AttributeType
 * @returns Short display name for the type group
 */
export function getShortTypeDisplayName(attributeType?: string): string {
  const group = getTypeCompatibilityGroup(attributeType);

  switch (group) {
    case 'integer':
      return 'Integer';
    case 'decimal':
      return 'Decimal/Money';
    case 'string':
      return 'Text';
    case 'boolean':
      return 'Boolean';
    case 'datetime':
      return 'Date/Time';
    case 'uniqueidentifier':
      return 'Lookup/GUID';
    case 'picklist':
      return 'Option Set';
    default:
      return 'Unknown';
  }
}
